import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { getGroupByCkNorme, getRevisioneCkByCkNorme } from '../common/helpers/norme-ck-lookup.helper';
import { checkDataFutura } from '../common/helpers/norme-validators.helper';
import { toNum } from '@shared/helpers/math.helper';
import { checkCkNorme, codiceFiscaleHelper, ruleGuard } from './amm-controllo-validators.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

const REVISORI_ARRAY_PATH = `${formPaths.ammControllo}.my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile`;

const my_AllElemRevisoriControlloContabileHelper = (form: FormGroup, targetPath: string, ckSrlAffidamContrContValues: number[], ckNormeTargetValue: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
    const ckSrlAffidamContrCont = toNum(getControl(amministrazioneControlloGroup, `${getGroupByCkNorme(ckNormeTargetValue)}.${getRevisioneCkByCkNorme(ckNormeTargetValue)}`).value);
    const my_AllElemRevisoriControlloContabile = getFormArray(form, REVISORI_ARRAY_PATH);

    my_AllElemRevisoriControlloContabile.controls.forEach((group: FormGroup) => {
        const targetControl = getControl(group, targetPath);
        const targetNameSplitted = targetPath?.split('.');
        const errorName = targetNameSplitted[targetNameSplitted?.length - 1];
        const checkCkSrlAffidamContrCont = ckSrlAffidamContrContValues.some((v) => v === ckSrlAffidamContrCont);
        const errors = targetControl.errors ? targetControl.errors : {};
        addRemoveError(isNilOrEmpty(targetControl?.value) && checkCkNorme(form, ckNormeTargetValue) && checkCkSrlAffidamContrCont, errors, errorName, {
            status: REQUIRED_STATUS,
        });

        targetControl.setErrors(isEqual(errors, {}) ? null : errors);
    });
};

const revisioneLegaleHelper = (form: FormGroup, targetPath: string, ckSrlAffidamContrContValue: number, ckNormeTargetValue: CkNormeValues) => {
    const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
    const targetControl = getControl(amministrazioneControlloGroup, targetPath);
    const targetNameSplitted = targetPath?.split('.');
    const errorName = targetNameSplitted[targetNameSplitted?.length - 1];
    const ckAffidamContrCont = toNum(getControl(amministrazioneControlloGroup, `${getGroupByCkNorme(ckNormeTargetValue)}.${getRevisioneCkByCkNorme(ckNormeTargetValue)}`).value);
    const errors = targetControl.errors ? targetControl.errors : {};
    addRemoveError(isNilOrEmpty(targetControl?.value) && checkCkNorme(form, ckNormeTargetValue) && ckAffidamContrCont === ckSrlAffidamContrContValue, errors, errorName, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

export const revisoriRules = (ckNormeTargetValue: CkNormeValues) => ({
    my_RagSocSocRevisione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            revisioneLegaleHelper(form, 'my_SocRevisioneControlloContabile.my_RagSocSocRevisione', 1, ckNormeTargetValue);
        });
    },

    my_DataAffIncarico: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            revisioneLegaleHelper(form, 'my_SocRevisioneControlloContabile.my_DataAffIncarico', 1, ckNormeTargetValue);
        });
    },

    my_AllRevisoriControlloContabileCognome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            my_AllElemRevisoriControlloContabileHelper(form, 'my_AllRevisoriControlloContabileCognome', [2, 3], ckNormeTargetValue);
        });
    },

    my_AllRevisoriControlloContabileNome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            my_AllElemRevisoriControlloContabileHelper(form, 'my_AllRevisoriControlloContabileNome', [2, 3], ckNormeTargetValue);
        });
    },

    my_AllRevisoriControlloContabileCodFisc: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const my_AllRevisoriControlloContabileCodFiscFormArray = getFormArray(form, REVISORI_ARRAY_PATH);

            my_AllRevisoriControlloContabileCodFiscFormArray.controls.map((group: FormGroup) => {
                codiceFiscaleHelper(group, 'my_AllRevisoriControlloContabileNome', 'my_AllRevisoriControlloContabileCognome', 'my_AllRevisoriControlloContabileCodFisc');
            });

            my_AllElemRevisoriControlloContabileHelper(form, 'my_AllRevisoriControlloContabileCodFisc', [2, 3], ckNormeTargetValue);
        });
    },

    my_AllRevisoriControlloContabileDataElezione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const my_AllRevisoriControlloContabileDataElezioneFormArray = getFormArray(form, REVISORI_ARRAY_PATH);
            my_AllRevisoriControlloContabileDataElezioneFormArray.controls.map((group: FormGroup) => {
                const dataElezioneControl = getControl(group, 'my_AllRevisoriControlloContabileDataElezione');
                const errors = dataElezioneControl.errors ? dataElezioneControl.errors : {};

                addRemoveError(checkDataFutura(dataElezioneControl.value), errors, 'my_AllRevisoriControlloContabileDataElezione', {
                    status: 'Non è consentita una data futura',
                });
            });

            my_AllElemRevisoriControlloContabileHelper(form, 'my_AllRevisoriControlloContabileDataElezione', [2, 3], ckNormeTargetValue);
        });
    },
});
